import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LcsApiService } from '@lcs/shared/api';
import { FileService } from '@lcs/shared/attachments';
import { Observable, combineLatest, of } from 'rxjs';
import { map, mapTo, take, tap } from 'rxjs/operators';
import { ReportDownloadTypes } from '../models/report-download-types';
import { CommonReportParameters } from '../models/report-parameters-base.interface';

@Injectable({ providedIn: 'root' })
export class ReportHttpService {
   constructor(private httpClient: HttpClient, private apiService: LcsApiService, private fileService: FileService) {}

   post<R, P>(url: string, parameters: P): Observable<R> {
      return this.httpClient.post<R>(this.buildUrl(url), this.buildParameters(parameters), {
         params: new HttpParams({
            fromObject: {
               // eslint-disable-next-line @typescript-eslint/naming-convention
               SerializeEnumsAsStrings: 'true',
            },
         }),
      });
   }

   get<R>(url: string): Observable<R> {
      return this.httpClient.get<R>(this.buildUrl(url), {
         params: new HttpParams({
            fromObject: {
               // eslint-disable-next-line @typescript-eslint/naming-convention
               SerializeEnumsAsStrings: 'true',
            },
         }),
      });
   }

   getSingleEntityValueForParameter(url: string, fields: string, fieldToFiler: string, id: string): Observable<any> {
      if (id && id.length > 0) {
         return this.httpClient.get(this.buildUrl(`/${url}/?fields=${fields}&filters=${fieldToFiler}=${id}`));
      } else {
         return of(null);
      }
   }

   fetchHtml<P>(url: string, parameters: P): Observable<Blob> {
      return this.httpClient.post(this.buildUrl(url), this.buildParameters(parameters), {
         // eslint-disable-next-line @typescript-eslint/naming-convention
         headers: { Accept: ReportDownloadTypes.Html },
         responseType: 'blob',
      });
   }

   fetchPdf<P>(url: string, parameters: P): Observable<Blob> {
      return this.httpClient.post(this.buildUrl(url), this.buildParameters(parameters), {
         // eslint-disable-next-line @typescript-eslint/naming-convention
         headers: { Accept: ReportDownloadTypes.Pdf },
         responseType: 'blob',
      });
   }

   fetchExcel<P>(url: string, parameters: P): Observable<Blob> {
      return this.httpClient.post(this.buildUrl(url), this.buildParameters(parameters), {
         // eslint-disable-next-line @typescript-eslint/naming-convention
         headers: { Accept: ReportDownloadTypes.Excel },
         responseType: 'blob',
      });
   }

   fetchExcelGet(url: string): Observable<Blob> {
      return this.httpClient.get(this.buildUrl(url), {
         // eslint-disable-next-line @typescript-eslint/naming-convention
         headers: { Accept: ReportDownloadTypes.Excel },
         responseType: 'blob',
      });
   }

   fetchHtmlGet(url: string): Observable<Blob> {
      return this.httpClient.get(this.buildUrl(url), {
         // eslint-disable-next-line @typescript-eslint/naming-convention
         headers: { Accept: ReportDownloadTypes.Html },
         responseType: 'blob',
      });
   }

   fetchPdfGet(url: string): Observable<Blob> {
      return this.httpClient.get(this.buildUrl(url), {
         // eslint-disable-next-line @typescript-eslint/naming-convention
         headers: { Accept: ReportDownloadTypes.Pdf },
         responseType: 'blob',
      });
   }

   downloadHtml<P>(url: string, parameters: P, filename: string): Observable<void> {
      return combineLatest([this.fetchHtml(url, parameters)]).pipe(
         take(1),
         map(([blob]) => {
            const name = filename;

            const fileInfo: [string, Blob] = [name, blob];
            return fileInfo;
         }),
         tap(([name, blob]) => {
            this.fileService.downloadBlob(blob, name + '.html');
         }),
         mapTo(undefined)
      );
   }

   downloadPdf<P>(url: string, parameters: P, filename: string): Observable<void> {
      return combineLatest([this.fetchPdf(url, parameters)]).pipe(
         take(1),
         map(([blob]) => {
            const name = filename;
            const fileInfo: [string, Blob] = [name, blob];
            return fileInfo;
         }),
         tap(([name, blob]) => {
            this.fileService.downloadBlob(blob, name + '.pdf');
         }),
         mapTo(undefined)
      );
   }

   downloadExcel<P>(url: string, parameters: P, filename: string): Observable<void> {
      return combineLatest([this.fetchExcel(url, parameters)]).pipe(
         take(1),
         map(([blob]) => {
            const name = filename;
            const fileInfo: [string, Blob] = [name, blob];
            return fileInfo;
         }),
         tap(([name, blob]) => {
            this.fileService.downloadBlob(blob, name + '.xlsx');
         }),
         mapTo(undefined)
      );
   }

   downloadHtmlGet(url: string, filename: string): Observable<void> {
      return this.fetchHtmlGet(url).pipe(
         tap((blob: Blob) => {
            this.fileService.downloadBlob(blob, filename + '.html');
         }),
         mapTo(undefined)
      );
   }

   downloadPdfGet(url: string, filename: string): Observable<void> {
      return this.fetchPdfGet(url).pipe(
         tap((blob: Blob) => {
            this.fileService.downloadBlob(blob, filename + '.pdf');
         }),
         mapTo(undefined)
      );
   }

   downloadExcelGet(url: string, filename: string): Observable<void> {
      return this.fetchExcelGet(url).pipe(
         tap((blob: Blob) => {
            this.fileService.downloadBlob(blob, filename + '.xlsx');
         }),
         mapTo(undefined)
      );
   }

   updateMemorizedReportUsageCount(memorizedReportId: number): Observable<void> {
      return this.httpClient.post<void>(
         this.buildUrl(`/MemorizedReports/${memorizedReportId}/UpdateUsageCount`),
         undefined
      );
   }

   private buildUrl(url: string): string {
      return this.apiService.apiUrl + url;
   }

   /**
    * Adds the `timeZone` parameter to the given parameters object.
    * Uses the browser's local time zone from the Intl API.
    * @param parameters The parameters object to extend.
    * @returns A shallow copy of the given parameters object, with the `timeZone` parameter added.
    */
   private buildParameters<P>(parameters: P): P & CommonReportParameters {
      return {
         ...parameters,
         timeZone: { value: Intl.DateTimeFormat().resolvedOptions().timeZone },
      };
   }
}
